import React, { useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Modal, StyleSheet, Text, TouchableOpacity, View } from './rn';
import ZoomablePhoto from './ZoomablePhoto';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Avatar from './Avatar';
import PageHeader from './PageHeader';
import { mediaUrl } from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';

type Props = { photoUrl: string | null; name: string; size: number; style?: StyleProp<ViewStyle> };

// A URL change unmounts the old viewer and its image callbacks. No new media
// access, export, download or profile-edit privilege is introduced here.
export default function ProfilePhotoButton(props: Props) {
  return <Photo key={props.photoUrl ?? ''} {...props} />;
}

function Photo({ photoUrl, name, size, style }: Props) {
  const colors = useTheme(); const {t} = useTranslation(); const insets = useSafeAreaInsets();
  const [open, setOpen] = useState(false);
  const uri = mediaUrl(photoUrl?.trim());
  if (!uri) return <Avatar name={name} size={size} style={style} />;
  return <>
    <TouchableOpacity testID="user-profile-photo-open" style={style} accessibilityRole="button"
      accessibilityLabel={t('profile.viewPhoto')} onPress={() => setOpen(true)}>
      <Avatar photoUrl={photoUrl} name={name} size={size} />
    </TouchableOpacity>
    {open && <Modal visible animationType="slide" onRequestClose={() => setOpen(false)}>
      <View testID="user-profile-photo-viewer" style={[styles.page, {backgroundColor: colors.white, paddingBottom: insets.bottom}]}>
        <PageHeader title={name} onBack={() => setOpen(false)} backTestID="user-profile-photo-close" />
        <PhotoImage uri={uri} />
      </View>
    </Modal>}
  </>;
}

function PhotoImage({uri}: {uri: string}) {
  const [attempt, setAttempt] = useState(0);
  return <ImageAttempt key={attempt} uri={uri} onRetry={() => setAttempt(value => value + 1)} />;
}

function ImageAttempt({uri, onRetry}: {uri: string; onRetry: () => void}) {
  const {t} = useTranslation(); const colors = useTheme();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  return <View style={styles.frame}>
    {state !== 'error' && <ZoomablePhoto imageTestID="user-profile-photo-image" uri={uri}
      accessibilityLabel={t('profile.viewPhoto')}
      onLoad={() => setState(previous => previous === 'error' ? previous : 'ready')} onError={() => setState('error')} />}
    {state === 'loading' && <Text style={[styles.loading, {color: colors.gray600}]}>{t('common.loading')}</Text>}
    {state === 'error' && <View style={styles.error}>
      <Text style={[styles.errorText, {color: colors.black}]}>{t('profile.photoUnavailable')}</Text>
      <TouchableOpacity testID="user-profile-photo-retry" accessibilityRole="button" style={styles.retry}
        onPress={onRetry}>
        <Text style={[styles.retryText, {color: colors.black}]}>{t('common.retry')}</Text>
      </TouchableOpacity>
    </View>}
  </View>;
}
const styles = StyleSheet.create({
  page: {flex: 1},
  frame: {flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center'},
  loading: {position: 'absolute', fontSize: 16},
  error: {padding: 24, gap: 16, alignItems: 'center'},
  errorText: {fontSize: 16, textAlign: 'center'},
  retryText: {fontSize: 16},
  retry: {minHeight: 48, minWidth: 48, padding: 12, alignItems: 'center', justifyContent: 'center'},
});
